import type { ReactNode } from 'react';
import { useTranslation } from '@/shared/i18n/use-translation';
import { Field } from '@/shared/ui/field/field';
import { FieldDescription } from '@/shared/ui/field/field-description';
import { FieldError } from '@/shared/ui/field/field-error';
import { FieldLabel } from '@/shared/ui/field/field-label';

type FieldShellProps = {
  readonly id: string;
  readonly label: string;
  readonly hint?: ReactNode;
  /** An English key (custom zod messages) or zod's own message in the locale; translated here. */
  readonly error?: string | undefined;
  readonly required?: boolean;
  readonly className?: string;
  readonly children: ReactNode;
};

/** Label, control, hint and error of one form field. */
export function FieldShell({ id, label, hint, error, required, className, children }: FieldShellProps) {
  const { t } = useTranslation();
  return (
    <Field data-invalid={error ? true : undefined} className={className}>
      <FieldLabel htmlFor={id}>
        {label}
        {required && (
          <span className="text-destructive" aria-hidden>
            *
          </span>
        )}
      </FieldLabel>
      {children}
      {hint && !error && <FieldDescription>{hint}</FieldDescription>}
      {error && <FieldError>{t(error)}</FieldError>}
    </Field>
  );
}
